import React from "react";
import { TrophyIcon, PencilLineIcon, HandCoinsIcon } from "lucide-react";
import { Battle, BattlesType, Claim } from "@dripit/types";
import isEmpty from "lodash.isempty";
import isNil from "lodash.isnil";

import { ClaimType } from "@dripit/constants";
import { cn } from "@/helpers/tailwind";
import { formatCurrency, usdcToUsd } from "@/helpers";
import { Contract, ContractFunc } from "@/types";
import { Button, ClaimRewardDialog } from "@/components/main";
import { useToast } from "@/components/ui";
import { useWeb3Query } from "@/hooks";

interface ClaimButtonProps {
  children?: React.ReactNode;
  label?: string;
  battleId?: Battle["onchainId"];
  battleType?: Battle["type"];
  claims: Array<Claim | null | undefined>;
  className?: string;
  hideIcon?: boolean;
  onClaimSuccess?: () => void;
}

export const ClaimButton: React.FC<ClaimButtonProps> = ({
  children,
  label,
  battleId = -1,
  battleType,
  claims,
  className,
  hideIcon = false,
  onClaimSuccess,
}) => {
  const { toast } = useToast();
  const [showClaimDialog, setShowClaimDialog] = React.useState(false);
  const [claimWin, { status: claimWinStatus }] = useWeb3Query(
    ContractFunc.CLAIM_WIN,
    { onSuccess: async () => onClaimSuccess?.() }
  );
  const [claimRefund, { status: claimRefundStatus }] = useWeb3Query(
    ContractFunc.CLAIM_REFUND,
    { onSuccess: async () => onClaimSuccess?.() }
  );
  const [claimMMIncentives, { status: claimMMIncentivesStatus }] = useWeb3Query(
    ContractFunc.CLAIM_MARKETMAKER_INCENTIVES,
    { onSuccess: async () => onClaimSuccess?.() }
  );
  const claim = React.useMemo(
    () =>
      claims?.filter(
        (claim) =>
          isNil(claim?.claimed_at) && claim?.type === ClaimType.BattleWin
      )?.[0] ??
      claims?.filter((claim) => isNil(claim?.claimed_at))?.[0] ??
      claims?.[0] ??
      null,
    [claims]
  );
  const status = React.useMemo(() => {
    if (claim?.type === ClaimType.BattleWin || claims.length > 1) {
      return claimWinStatus;
    } else if (claim?.type === ClaimType.BattleMarketMaker) {
      return claimMMIncentivesStatus;
    } else if (claim?.type === ClaimType.Refund) {
      return claimRefundStatus;
    }
    return "idle";
  }, [
    claim?.type,
    claims,
    claimWinStatus,
    claimMMIncentivesStatus,
    claimRefundStatus,
  ]);
  const hasBeenClaimed = React.useMemo(
    () => !isNil(claim?.claimed_at),
    [claim]
  );

  const handleSingleClaim = async () => {
    if (hasBeenClaimed || isNil(claim)) return;
    const localBattleId = claim?.battle?.onchainId ?? battleId;
    setShowClaimDialog(true);
    const localBattleType = battleType ?? claim?.battle?.type;

    let contractAddress;
    switch (localBattleType) {
      case BattlesType.head2head_battle:
        contractAddress = Contract.HEAD2HEAD_BATTLES;
        break;
      case BattlesType.hypeboard_battle:
        contractAddress = Contract.HYPEBOARD_BATTLES;
        break;
      default:
        console.error("Invalid battle type", localBattleType);
        setShowClaimDialog(false);
        toast({
          title: "Error",
          description:
            "Something went wrong while claiming your reward or refund.",
          variant: "destructive",
        });
        return;
    }
    if (claim?.type === ClaimType.BattleWin) {
      await claimWin(contractAddress, { battleIds: [localBattleId] });
    } else if (claim?.type === ClaimType.BattleMarketMaker) {
      await claimMMIncentives(Contract.HEAD2HEAD_BATTLES, {
        battleIds: [localBattleId],
      });
    } else if (claim?.type === ClaimType.Refund) {
      await claimRefund(contractAddress, { battleIds: [localBattleId] });
    }
  };

  const handleClaim = async () => {
    try {
      if (!isEmpty(claims) && claims.length === 1) {
        await handleSingleClaim();
      } else if (!isEmpty(claims) && claims.length > 1) {
        setShowClaimDialog(true);
        const battleIds = claims.reduce(
          (acc, claim) => {
            if (!claim || !isNil(claim.claimed_at)) return acc;

            let battleType, claimType;

            switch (claim?.battle?.type) {
              case BattlesType.head2head_battle:
                battleType = "head2head";
                break;
              case BattlesType.hypeboard_battle:
                battleType = "hypeboard";
                break;
              default:
                return acc;
            }

            switch (claim.type) {
              case ClaimType.Refund:
                claimType = "Refunds";
                break;
              case ClaimType.BattleWin:
                claimType = "Wins";
                break;
              case ClaimType.BattleMarketMaker:
                claimType = "MMIncentives";
                break;
              default:
                return acc;
            }

            const key = `${battleType}${claimType}` as keyof typeof acc;

            return {
              ...acc,
              [key]: [...(acc[key] ?? []), claim?.battle?.onchainId ?? -1],
            };
          },
          {
            head2headRefunds: [] as number[],
            head2headWins: [] as number[],
            head2headMMIncentives: [] as number[],
            hypeboardRefunds: [] as number[],
            hypeboardWins: [] as number[],
          }
        );

        const promises = [
          !isEmpty(battleIds.head2headWins) &&
            (await claimWin(Contract.HEAD2HEAD_BATTLES, {
              battleIds: battleIds.head2headWins,
            })),
          !isEmpty(battleIds.head2headMMIncentives) &&
            (await claimMMIncentives(Contract.HEAD2HEAD_BATTLES, {
              battleIds: battleIds.head2headMMIncentives,
            })),
          !isEmpty(battleIds.head2headRefunds) &&
            (await claimRefund(Contract.HEAD2HEAD_BATTLES, {
              battleIds: battleIds.head2headRefunds,
            })),
          !isEmpty(battleIds.hypeboardWins) &&
            (await claimWin(Contract.HYPEBOARD_BATTLES, {
              battleIds: battleIds.hypeboardWins,
            })),
          !isEmpty(battleIds.hypeboardRefunds) &&
            (await claimRefund(Contract.HYPEBOARD_BATTLES, {
              battleIds: battleIds.hypeboardRefunds,
            })),
        ].filter(Boolean);
        console.log({ mstatus: "batch claim", battleIds, promises });
        await Promise.all(promises);
      }
    } catch (error) {
      console.error(error);
      setShowClaimDialog(false);
      toast({
        title: "Error",
        description:
          "Something went wrong while claiming your rewards or refunds.",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    if (!isEmpty(children)) return children;
    if (claim?.type === ClaimType.Refund) {
      return (
        <>
          <HandCoinsIcon className="size-4" />
          <span>{label ?? "Refund"}</span>
        </>
      );
    }

    return (
      <>
        {claim?.type === ClaimType.BattleMarketMaker ? (
          <PencilLineIcon className="size-4" />
        ) : (
          <TrophyIcon className="size-4" />
        )}
        <span>
          {label ??
            (!isEmpty(claims) && claims.length > 1 ? "Claim All" : "Claim")}
        </span>
      </>
    );
  };

  if (isNil(claim)) return children ?? null;

  return (
    <>
      <Button
        className={cn(
          "text-slate-900 hover:text-slate-900",
          "flex rounded-xl p-2 lg:rounded-md",
          className,
          {
            "[&>svg]:hidden": hideIcon,
            "bg-teal-400 hover:bg-teal-400/80":
              claim?.type !== ClaimType.Refund && isEmpty(children),
            "bg-orange-400 hover:bg-orange-400/80":
              claim?.type === ClaimType.Refund && isEmpty(children),
          }
        )}
        disabled={
          ["pending", "success"].includes(status) ||
          hasBeenClaimed ||
          isNil(claim)
        }
        loading={status === "pending"}
        onClick={handleClaim}>
        {renderContent()}
      </Button>
      <ClaimRewardDialog
        isOpen={showClaimDialog}
        onOpenChange={() => setShowClaimDialog(false)}
        status={status}
        claimType={claim?.type}
        rewardAmount={formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
      />
    </>
  );
};
